import React from "react";
import {
  isQuestionMsgStepId,
  PersonalizationValue,
  TriggerStepCreate,
} from "@dots-projects/types";
import { useForm } from "react-hook-form";
import produce from "immer";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { SendStepFormWithCommonParts } from "./SendStepFormWithCommonParts";

/**
 * Renders the form for a "Send message" step. When the form is submitted, generate the step data and publish an update action to add a new step to the trigger flow.
 */
export function StepFormSendMessage({
  step,
  insertVariables,
}: {
  step: TriggerStepCreate;
  insertVariables: PersonalizationValue[];
}) {
  invariant(step.type === "send_message");

  const defaultValues = {
    name: step.name,
    htmlMessage: step.configs.$$editorHtmlMessage,
    message: step.configs.message,
    ephemeral: !!step.configs.ephemeral,
    channelId: step.configs.channelId,
    removeUrlEmbeds: step.configs.removeUrlEmbeds,
    delayMinutes:
      typeof step.configs.delaySeconds === "number"
        ? step.configs.delaySeconds / 60
        : 0,
    delayed:
      typeof step.configs.delaySeconds === "number" &&
      step.configs.delaySeconds > 0,
  };

  const form = useForm({
    defaultValues,
    mode: "onChange",
  });
  const { handleSubmit } = form;
  const { dispatch, onPersist } = useTriggerEditorStateDispatch();

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      stepDraft.configs.ephemeral = data.ephemeral;
      stepDraft.configs.channelId = data.channelId;
      stepDraft.configs.message = data.message;
      stepDraft.configs.$$editorHtmlMessage = data.htmlMessage;
      stepDraft.configs.removeUrlEmbeds = data.removeUrlEmbeds;
      stepDraft.configs.delaySeconds = data.delayMinutes
        ? data.delayMinutes * 60
        : 0;
    });
    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
      customTitle={isQuestionMsgStepId(step.id) ? "Send a question" : undefined}
    >
      <SendStepFormWithCommonParts
        step={step}
        form={form}
        insertVariables={insertVariables}
        ephemeralCheckbox={!isQuestionMsgStepId(step.id)}
      ></SendStepFormWithCommonParts>
    </StepFormWithCommonParts>
  );
}
